<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import { acquireZendeskHide, releaseZendeskHide } from '@Src/shared/utils/zendeskVisibility';

const props = withDefaults(defineProps<{
    open: boolean;
    // Float the mobile drag handle over the sheet content instead of
    // reserving vertical space for it. Used when content (e.g. a priming
    // illustration) needs to sit flush to the top edge of the sheet.
    overlayHandle?: boolean;
    // Full-viewport variant. Mobile: fills the screen bottom-to-top with
    // square corners (instead of the 80vh drawer with rounded top). Desktop:
    // fills the viewport instead of the 420px centered modal. Drag handle
    // is hidden. Used by completion views — the prototype positions those
    // with `inset: 0` on both breakpoints.
    fullscreen?: boolean;
    // Suppress the top-right × button at ≥768px. Used by completion views
    // where a sidebar-level back button owns the close affordance on
    // desktop. Mobile still shows the ×.
    hideCloseOnDesktop?: boolean;
    // Accessible name for the dialog. Pairs with role="dialog" on .bsm so
    // screen readers announce the sheet with context (e.g. "Select property
    // dialog") instead of just "dialog". Optional but every consumer should
    // pass one — if omitted, NVDA will announce only "dialog".
    dialogLabel?: string;
    // When false, suppress all user-driven close affordances: Escape key,
    // backdrop click, and the × button. Used by the welcome drawer where
    // an explicit consent action is required before the sheet may close.
    // Parent must drive `open` directly to dismiss.
    //
    // Also opts the sheet out of the DS-5361 Zendesk hide — a sheet with no
    // way out must not also take away the support channel. Setting this to
    // false therefore has two effects, not one.
    dismissable?: boolean;
    // Opt out of the DS-5361 Zendesk hide for sheets that read as destination
    // screens rather than transient drawers — the completion receipts, whose
    // route-level twins in request-mfe keep the launcher. See the block below.
    keepZendesk?: boolean;
}>(), {
    overlayHandle: false,
    fullscreen: false,
    hideCloseOnDesktop: false,
    dialogLabel: undefined,
    dismissable: true,
    keepZendesk: false,
});

const emit = defineEmits<{
    'update:open': [value: boolean];
}>();

const close = () => {
    emit('update:open', false);
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && props.open && props.dismissable) {
        close();
    }
};

const handleBackdropClick = () => {
    if (props.dismissable) {
        close();
    }
};

watch(() => props.open, (val) => {
    if (val) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeydown);
    } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleKeydown);
    }
}, { immediate: true });

// DS-5361: the Zendesk launcher floats above every sheet and covers the primary
// CTA — reported on task priming and the property picker. Hide it while a sheet
// is open; the ref count in zendeskVisibility keeps overlapping sheets from
// un-hiding it out from under each other.
//
// Two exemptions:
//   - Non-dismissable sheets. The welcome consent gate has no ×, no Escape, and
//     no backdrop close, so hiding the launcher there would leave a participant
//     with no exit and no support channel at the same time.
//   - keepZendesk. The completion receipts mirror route-level screens in
//     request-mfe (e.g. "Your bank account details") that keep the launcher;
//     hiding it on only one of the two would be arbitrary from the participant's
//     side.
let hasAcquiredZendeskHide = false;

const syncZendeskHide = (shouldHide: boolean) => {
    if (shouldHide === hasAcquiredZendeskHide) return;
    hasAcquiredZendeskHide = shouldHide;
    if (shouldHide) {
        acquireZendeskHide();
    } else {
        releaseZendeskHide();
    }
};

watch(
    () => props.open && props.dismissable && !props.keepZendesk,
    syncZendeskHide,
    { immediate: true },
);

// Release scroll lock + drop listener if the sheet unmounts while open
// (e.g. route change mid-transition) — otherwise body.style.overflow stays
// 'hidden' forever and the listener leaks. Scroll-lock release is guarded
// on `open` so we don't stomp on another modal's lock if this instance was
// already closed; removeEventListener is idempotent so it stays unguarded.
onBeforeUnmount(() => {
    if (props.open) {
        document.body.style.overflow = '';
    }
    document.removeEventListener('keydown', handleKeydown);
    // Guarded by hasAcquiredZendeskHide so a close-then-unmount can't release twice.
    syncZendeskHide(false);
});
</script>

<template>
    <Teleport to="body">
        <Transition name="portal-sheet">
            <div
                v-if="open"
                class="bsm-overlay"
                :class="{ 'bsm-overlay--fullscreen': fullscreen }"
                @click.self="handleBackdropClick"
            >
                <div
                    class="bsm"
                    role="dialog"
                    aria-modal="true"
                    :aria-label="dialogLabel"
                    :class="{
                        'bsm--overlay-handle': overlayHandle,
                        'bsm--fullscreen': fullscreen,
                        'bsm--hide-close-on-desktop': hideCloseOnDesktop,
                    }"
                >
                    <div v-if="!fullscreen" class="bsm__handle" />
                    <button v-if="dismissable" class="bsm__close portal-nav-btn" @click="close" aria-label="Close">
                        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div class="bsm__content">
                        <slot />
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;


.bsm-overlay {
    position: fixed;
    inset: 0;
    background: $portal-scrim;
    z-index: 200;
    display: flex;
    align-items: flex-end;

    @media (min-width: 768px) {
        align-items: center;
        justify-content: center;
        background: $portal-scrim-desktop;
        backdrop-filter: blur($portal-scrim-blur);
        -webkit-backdrop-filter: blur($portal-scrim-blur);
    }

    // Fullscreen: the sheet covers the viewport, nav included. PortalHeader only sticks
    // while the page is at the top, so offsetting below it exposes page behind the sheet.
    &--fullscreen {
        align-items: stretch;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;

        @media (min-width: 768px) {
            align-items: stretch;
        }
    }
}

.bsm {
    width: 100%;
    max-height: 80vh;
    background: $portal-surface;
    border-radius: $portal-surface-card-radius $portal-surface-card-radius 0 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    box-shadow: $portal-modal-shadow;

    // Short-viewport phones (iPhone SE through iPhone 14 Pro / 15 Pro at
    // 852px): give the sheet extra vertical room so long content (e.g.
    // Welcome sheet's Terms/Privacy footer) isn't clipped below the visible
    // viewport. dvh keeps the sizing honest under iOS Safari's collapsing
    // chrome.
    @media (max-height: 900px) {
        max-height: 90dvh;
    }

    @media (min-width: 768px) {
        width: 420px;
        max-height: 90vh;
        border-radius: $portal-modal-radius;
    }

    &--fullscreen {
        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100%;
        border-radius: 0;
        box-shadow: none;

        @media (min-width: 768px) {
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
        }
    }

    &__handle {
        width: 36px;
        height: 4px;
        background: $portal-border;
        border-radius: 2px;
        margin: 10px auto 0;
        flex-shrink: 0;

        @media (min-width: 768px) {
            display: none;
        }
    }

    // overlayHandle: lift the handle out of the flex flow so content starts
    // at the top of the sheet. Handle floats over whatever renders first.
    &--overlay-handle &__handle {
        position: absolute;
        top: 10px;
        left: 50%;
        transform: translateX(-50%);
        margin: 0;
        z-index: 1;
    }

    // .portal-nav-btn (global) provides size, shape, and the transparent
    // → $portal-bg hover that matches the sidebar / completion-sheet back
    // button. Local rules only pin position within the sheet.
    &__close {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 1;

        svg {
            width: 18px;
            height: 18px;
        }
    }

    // Hide the × above 768px when a container-level affordance owns
    // close (e.g. the completion sheet's sidebar back arrow). Mobile
    // still shows the × since there's no sidebar there.
    &--hide-close-on-desktop &__close {
        @media (min-width: 768px) {
            display: none;
        }
    }

    &__content {
        // Bottom padding floors at 32px; on notched iOS (when the host opts in
        // to viewport-fit=cover) it grows to clear the home indicator + 0.5rem
        // of breathing room. env() resolves to 0 without viewport-fit=cover.
        padding: $portal-section-py $portal-section-px max(32px, calc(env(safe-area-inset-bottom) + 0.5rem));
        overflow-y: auto;

        @media (min-width: 768px) {
            padding: 24px 32px 32px;
        }
    }

    // Fullscreen: surrounding sheet padding would offset content from the
    // viewport edges and constrain the sidebar / hero. Each fullscreen
    // consumer manages its own internal padding.
    &--fullscreen &__content {
        padding: 0;

        @media (min-width: 768px) {
            padding: 0;
        }
    }
}

// Transition `portal-sheet` is defined globally in -styles/_animations.scss
// and keyed on the `.bsm` root class — no scoped override needed here.
</style>
